///
/// EarthBrowser
///
/// Created by Matt Giger on 11/4/2026
/// Copyright (c) 2026 Geodata Labs
///
/// What to do on the launch after a crash (detected by CrashSentinel).
/// Modeled on how browsers, Android's Rescue Party and games recover: a
/// single crash is treated as noise and costs the user nothing, only a
/// loop of crashes escalates, and the mitigation applies to this launch
/// alone - a session that proves stable clears the history. Nothing here
/// persists a penalty, and the user's layer set is never silently lost.
///
/// Levels (consecutive crashes inside the sentinel's loop window):
///
/// 1. Staggered restore. Heavy layers are taken off the globe, the app
///    comes up light, then they return one at a time once the scene is
///    idle, with a crumb and a "restoring" marker between each. Nothing is
///    shown to the user; the layers are back within seconds. If the tab
///    dies again the marker names the layer precisely.
/// 2. Lite mode + suspect last. As level 1, but the session runs with
///    minimum preload windows (MemoryBudget lite mode) and the suspected
///    layer - the one being restored at the kill, else the most recently
///    enabled - goes last. A toast says so and offers to skip it.
/// 3. Start light. Heavy layers stay off; a toast offers to restore them
///    (staggered, in lite mode). The owed set is kept by the sentinel so a
///    further crash still knows it.
///
/// Stability: after the restore completes (or at once when there is
/// nothing to restore) a timer counts foreground time; once it has run its
/// course the loop history is forgotten and the next launch starts clean.

import { showRecoveringToast, showStartLightToast } from "@/components/ui/crash-recovery-toast"
import { crashVerdict, heavyLayersEnabled, markStable, noteRestoring, recordCrumb, setPendingRestore } from "@/earthbrowser/utils/CrashSentinel"
import { datasetDisplayName, isHeavyDataset, setLiteMode } from "@/earthbrowser/utils/MemoryBudget"
import { datasetTypeForLayerId } from "@/hooks/useLayerLoadStates"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { appStore } from "@/redux/store"
import { validateDatasetArray } from "@/redux/validation/earthBrowserValidation"
import { type DatasetType } from "@/types/DatasetElement"
import { track } from "@/utils/analytics"

/** Foreground time a session must survive before the crash loop is forgotten */
const cStabilityMs = 90_000
/** Re-check interval when the stability deadline passes with the page hidden */
const cStabilityRecheckMs = 30_000
/** Idle time after the scene exists before the first layer comes back */
const cSceneSettleMs = 2_500
/** Longest wait for the light scene's own loads to finish first (entity
 *  layers keep trickling; the user should not wait on them for long) */
const cSceneIdleTimeoutMs = 6_000
/** Longest wait for a restored layer to start reporting a load (hidden tabs
 *  get no frames, so it may never) */
const cLayerStartTimeoutMs = 2_000
/** Longest wait for one restored layer to finish loading before the next starts */
const cLayerSettleTimeoutMs = 15_000
/** Breathing room between layers so their uploads don't overlap */
const cBetweenLayersMs = 1_500
const cPollMs = 250
/** Longest wait for the layer manager (scene) to exist before restoring anyway */
const cSceneTimeoutMs = 30_000

type Phase = "idle" | "waiting" | "restoring" | "offered" | "done"

let phase: Phase = "idle"
let queue: DatasetType[] = []
let restored: DatasetType[] = []
let skipped: DatasetType[] = []
let stableAt: number | null = null
let stabilityTimer: number | null = null

function sleep(ms: number): Promise<void> {
	return new Promise(resolve => window.setTimeout(resolve, ms))
}

/** Resolve once `test` passes or `timeoutMs` elapses */
async function waitFor(test: () => boolean, timeoutMs: number): Promise<void> {
	const deadline = performance.now() + timeoutMs
	while (performance.now() < deadline) {
		if (test()) return
		await sleep(cPollMs)
	}
}

function layerManager() {
	return appStore.getState().datasetScene.layerManager
}

/** Whether any visible engine layer of this type still reports pending work */
function layerLoading(type: DatasetType): boolean {
	const reports = layerManager()?.getLoadStates() ?? []
	return reports.some(report => report.visible && datasetTypeForLayerId(report.id) === type && report.phase !== "error")
}

function anyVisibleLoading(): boolean {
	const reports = layerManager()?.getLoadStates() ?? []
	return reports.some(report => report.visible && report.phase !== "error")
}

/**
 * Start the stability clock. Counts from now; if the deadline passes with
 * the page hidden (backgrounded phones don't crash in the foreground, so
 * that time proves nothing) it re-arms until a foreground check passes.
 */
function armStability(): void {
	if (stabilityTimer !== null) window.clearTimeout(stabilityTimer)
	const check = () => {
		stabilityTimer = null
		if (document.visibilityState !== "visible") {
			stabilityTimer = window.setTimeout(check, cStabilityRecheckMs)
			return
		}
		stableAt = Date.now()
		markStable()
		recordCrumb("recovery:stable")
	}
	stabilityTimer = window.setTimeout(check, cStabilityMs)
}

/** Bring the queued layers back one at a time, then start the stability clock */
async function restoreStaggered(): Promise<void> {
	phase = "waiting"
	await waitFor(() => layerManager() !== null, cSceneTimeoutMs)
	await sleep(cSceneSettleMs)
	// Let whatever the light scene is still loading (tiles, entity layers) land first
	await waitFor(() => !anyVisibleLoading(), cSceneIdleTimeoutMs)

	phase = "restoring"
	while (queue.length > 0) {
		const layer = queue.shift()!
		setPendingRestore(queue)
		noteRestoring(layer)
		recordCrumb(`restore:${layer}`)
		appStore.dispatch(sendDatasetAction({ type: layer, action: "enable" }))
		// The enable may have been refused (memory gate); otherwise the layer
		// starts loading on the next frame. Wait for it to start, then settle
		await waitFor(() => layerLoading(layer), cLayerStartTimeoutMs)
		await waitFor(() => !layerLoading(layer), cLayerSettleTimeoutMs)
		noteRestoring(null)
		restored.push(layer)
		recordCrumb(`restored:${layer}`)
		if (queue.length > 0) await sleep(cBetweenLayersMs)
	}
	setPendingRestore([])
	phase = "done"
	track("crash_recovery", { level: crashVerdict().level, outcome: "restored", layers: restored, suspect: crashVerdict().suspect ?? undefined })
	armStability()
}

/**
 * Decide and start this launch's recovery. Call once the persisted state
 * has rehydrated (StoreProvider onBeforeLift) and before the scene exists,
 * so heavy layers never allocate before the decision.
 */
export function beginCrashRecovery(): void {
	const verdict = crashVerdict()

	if (verdict.level === 0) {
		// Clean start. If a loop is still in the window, this session can
		// close it by proving stable
		if (verdict.history.length > 0) armStability()
		return
	}

	// The user's full heavy set: what rehydrated plus what an interrupted
	// recovery still owed, in enable order
	const visible = appStore.getState().earthbrowser?.visibleDatasets ?? []
	const wanted: DatasetType[] = visible.filter(isHeavyDataset)
	for (const layer of validateDatasetArray(verdict.heavyLayers)) {
		if (isHeavyDataset(layer) && !wanted.includes(layer)) wanted.push(layer)
	}
	const suspect = verdict.suspect !== null && wanted.includes(verdict.suspect as DatasetType) ? verdict.suspect as DatasetType : null
	const suspectName = suspect ? datasetDisplayName(suspect) : null

	// Off the globe now; the queue brings them back with the suspect last
	queue = suspect ? [...wanted.filter(layer => layer !== suspect), suspect] : [...wanted]
	restored = []
	skipped = []
	for (const layer of wanted) {
		appStore.dispatch(sendDatasetAction({ type: layer, action: "disable" }))
	}
	setPendingRestore(queue)
	recordCrumb(`recovery:start level=${verdict.level} queue=${queue.join(",")}`)
	console.warn(`[CrashRecovery] level ${verdict.level}: restoring ${queue.join(", ") || "nothing"}${suspect ? ` (suspect ${suspect})` : ""}`)

	if (verdict.level >= 2) setLiteMode(true)

	if (verdict.level >= 3 && queue.length > 0) {
		// Start light: the layers wait for the user. The Toaster mounts
		// with the app; give it a moment
		phase = "offered"
		armStability()
		window.setTimeout(() => {
			showStartLightToast(queue.length, suspectName, () => {
				track("crash_recovery", { level: verdict.level, outcome: "restore_requested", layers: queue, suspect: suspect ?? undefined })
				void restoreStaggered()
			})
		}, 1500)
		return
	}

	if (verdict.level === 2 && queue.length > 0) {
		window.setTimeout(() => {
			showRecoveringToast(suspectName, () => {
				if (!suspect) return
				if (queue.includes(suspect)) {
					queue = queue.filter(layer => layer !== suspect)
					skipped.push(suspect)
					setPendingRestore(queue)
				} else if (restored.includes(suspect)) {
					appStore.dispatch(sendDatasetAction({ type: suspect, action: "disable" }))
					restored = restored.filter(layer => layer !== suspect)
					skipped.push(suspect)
				}
				track("crash_recovery", { level: verdict.level, outcome: "suspect_skipped", layers: [suspect], suspect })
			})
		}, 1500)
	}

	if (queue.length === 0) {
		// Nothing heavy was on: nothing to stagger, just see whether this session holds
		phase = "done"
		armStability()
		return
	}
	void restoreStaggered()
}

/** Diagnostic snapshot for window.__ebDebug */
export function crashRecoverySnapshot(): Record<string, unknown> {
	return {
		phase,
		level: crashVerdict().level,
		suspect: crashVerdict().suspect,
		queue: [...queue],
		restored: [...restored],
		skipped: [...skipped],
		heavyOn: heavyLayersEnabled(),
		stableAt,
		stabilityArmed: stabilityTimer !== null,
	}
}
